// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getCommon } from "@hardwoods/App/Store/Selectors/CommonState";
import { color } from "@hardwoods/XDL/Utilities";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import {
    emailValidation,
    maxLengthAttr,
    passwordValidation,
    userNameValidation,
} from "@hardwoods/XDL/Utilities/validation";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Link, { LinkPresentationProps } from "@insite/mobius//Link";
import Modal, { ModalPresentationProps } from "@insite/mobius//Modal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps } from "styled-components";

const StyledForm = getStyledWrapper("form");

interface OwnProps {
    extendedStyles?: SignInComponentStyles;
    isSignInPage?: true;
    changePasswordInstructions?: string;
    fullWidthButton?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : encodeURIComponent(`${location.pathname}${location.search}`);
    return {
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        isMobile: getCommon(state).isMobile,
        settingsCollection: getSettingsCollection(state),
        externalError: parsedQuery.externalError,
        returnUrl,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
    };
};

const mapDispatchToProps = {
    signIn,
};

export interface SignInComponentStyles {
    titleWrapper?: InjectableCss;
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupPresentationProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    registrationButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    registerButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
}

export const signInComponentStyles: SignInComponentStyles = {
    titleWrapper: {
        css: css`
            color: ${color("text.main")};
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 20px;
            margin-bottom: 22px;
        `,
    },
    signInGridContainer: { gap: 20 },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInButton: {
        sizeVariant: "large",
        css: css`
            width: 100%;
        `,
    },
    registrationButton: {
        sizeVariant: "medium",
        css: css`
            margin: 0 auto;
            padding: 0 10px;
            width: 100%;
        `,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            align-items: flex-end;
            position: relative;
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    registerButtonGridItem: {
        width: 12,
    },
    rememberMeGridItem: {
        width: 12,
    },
    rememberMeCheckboxGroup: {
        css: css`
            legend {
                display: none;
            }
        `,
    },
    forgotPasswordGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
        `,
    },
    forgotPasswordLink: {
        color: "secondary",
        typographyProps: {
            size: 12,
        },
        css: css`
            margin-top: 10px;
        `,
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const XngSignIn: FC<Props> = ({
    settingsCollection,
    externalError,
    createAccountUrl,
    signIn,
    signInUrl,
    isMobile,
    extendedStyles,
    isSignInPage,
    changePasswordInstructions,
    returnUrl,
    isConfirmationPage,
    homePageUrl,
    theme,
    fullWidthButton,
}) => {
    const [styles] = useState(() => mergeWithCss(signInComponentStyles, extendedStyles));
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");
    const history = useHistory();
    const accountSettings = settingsCollection.accountSettings;
    const customSetting: any = settingsCollection.customSettings;
    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const { showRegistrationLink } = customSetting;

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (!isEmailValid(userName) || !passwordValidation(pass, true)) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        const queryParams = parseQueryString<{ returnUrl?: string }>(window.location.search);
        const returnUrl = removeAbsoluteUrl(queryParams.returnUrl?.toString());

        setErrorMessage("");

        const prefix = customSetting?.usernamePrefix;

        signIn({
            userName: `${prefix || ""}${userName}`,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: theme.signIn?.useOverlay && isConfirmationPage ? homePageUrl : returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        hideTippy();
        history.push(createAccountUrl);
    };

    const isEmailValid = (value: string) => {
        if (accountSettings.useEmailAsUserName) {
            return emailValidation(value);
        }
        return userNameValidation(userName);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };
    const userErrorMessage = validateUserName();

    const passwordErrorMessage =
        !password && isSignInClicked
            ? siteMessage("SignInInfo_Password_Required")
            : !passwordValidation(password)
            ? siteMessage("SignInInfo_Password_Validation")
            : errorMessage || "";

    const titleMessage = showRegistrationLink ? translate("Sign In or Register An Account") : translate("Sign In");

    const signInButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate data-test-selector="header_signIn">
                {!isSignInPage && (
                    <StyledWrapper {...styles.titleWrapper}>
                        {isMobile ? (
                            <Link href={signInUrl ?? ""} color="text.main">
                                {titleMessage}
                            </Link>
                        ) : (
                            <Typography>{titleMessage}</Typography>
                        )}
                    </StyledWrapper>
                )}

                <GridContainer {...styles.signInGridContainer}>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            id={isSignInPage ? "userName-signInPage" : "userName"}
                            {...styles.userNameTextField}
                            placeholder={
                                accountSettings.useEmailAsUserName
                                    ? translate("Your Email")
                                    : translate("Your Username")
                            }
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")}
                            onChange={e => {
                                if (e.currentTarget.value.length > maxLengthAttr.email) {
                                    return;
                                }
                                setUserName(e.currentTarget.value);
                            }}
                            value={userName}
                            error={userErrorMessage}
                            autoComplete="username"
                            data-test-selector="signIn_userName"
                            maxLength={
                                accountSettings.useEmailAsUserName ? maxLengthAttr.email : maxLengthAttr.userName
                            }
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            id={isSignInPage ? "password-signInPage" : "password"}
                            {...styles.passwordTextField}
                            label={translate("Password")}
                            placeholder={translate("Your Password")}
                            onChange={e => {
                                if (e.currentTarget.value.length > maxLengthAttr.password) {
                                    return;
                                }
                                setPassword(e.currentTarget.value);
                            }}
                            value={password}
                            error={passwordErrorMessage}
                            iconProps={{
                                ...styles.icon,
                                src: showPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signIn_password"
                            type={showPassword ? "text" : "password"}
                            maxLength={maxLengthAttr.password}
                        />
                        <Link
                            {...styles.forgotPasswordLink}
                            type="button"
                            onClick={() => setIsResetPasswordModalOpen(true)}
                            data-test-selector="signIn_forgotPassword"
                        >
                            {translate("Forgot Password")}
                        </Link>
                        <Modal
                            headline={translate("Reset Password")}
                            {...styles.resetPasswordModal}
                            isOpen={isResetPasswordModalOpen}
                            handleClose={() => onResetPasswordClose()}
                            data-test-selector="resetPasswordModal"
                        >
                            <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                        </Modal>
                    </GridItem>
                    <GridItem {...styles.rememberMeGridItem}>
                        {showRememberMe && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="rememberMe"
                                    {...styles.rememberMeCheckbox}
                                    checked={rememberMe}
                                    onChange={(_, value) => setRememberMe(value)}
                                >
                                    {translate("Remember Me")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                        {showKeepMeSignedIn && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="keepMeSignedIn"
                                    {...styles.rememberMeCheckbox}
                                    checked={keepMeSignedIn}
                                    onChange={(_, value) => setKeepMeSignedIn(value)}
                                >
                                    {translate("Keep Me Signed In")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                    </GridItem>
                    <GridItem {...styles.signInButtonGridItem}>
                        <Button
                            {...styles.signInButton}
                            css={signInButtonCss}
                            type="submit"
                            data-test-selector="signIn_submit"
                        >
                            {translate("Sign In")}
                        </Button>
                        <Modal
                            headline={translate("Change Password")}
                            {...styles.changePasswordModal}
                            isOpen={isChangePasswordModalOpen}
                            handleClose={onChangePasswordClose}
                        >
                            <SignInChangePasswordForm
                                enteredUserName={userName}
                                enteredPassword={password}
                                instructionsText={
                                    changePasswordInstructions ??
                                    translate("A password change is required for your account before you may continue.")
                                }
                                onPasswordChanged={onPasswordChanged}
                            />
                        </Modal>
                    </GridItem>
                    {!isSignInPage && showRegistrationLink && (
                        <GridItem {...styles.registerButtonGridItem}>
                            <Button
                                variant="tertiary"
                                type="button"
                                {...styles.registrationButton}
                                onClick={handleCreateNewAccount}
                                data-test-selector="signIn_register"
                            >
                                {translate("Register An Account")}
                            </Button>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledForm>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(XngSignIn);
